import {BaseAlert} from "@/components/BaseAlert";
import {BaseButton} from "@/components/BaseButton";
import {BaseCard} from "@/components/BaseCard";
import {PATHS} from "@/constants/routes";
import {useCreateCampaign, usePreviewAudience} from "@/hooks/usePush";
import type {
  AudienceFilters,
  AudienceType,
  CampaignCategory,
  CampaignPriority,
  CreateCampaignDto,
  DataPayload,
} from "@/types/push.types";
import {ArrowLeft, ArrowRight, Send} from "lucide-react";
import {useState} from "react";
import {useNavigate} from "react-router";
import {Step1Content} from "./components/Step1Content";
import {Step2Audience} from "./components/Step2Audience";
import {Step3Review} from "./components/Step3Review";

type WizardStep = 1 | 2 | 3;

interface CampaignForm {
  title: string;
  body: string;
  category: CampaignCategory;
  priority: CampaignPriority;
  imageUrl?: string;
  actionUrl?: string;
  dataPayload?: DataPayload;
  audienceType: AudienceType;
  audienceFilters?: AudienceFilters;
  targetUserId?: string;
}

export const CampaignCreator = () => {
  const navigate = useNavigate();
  const createCampaign = useCreateCampaign();
  const previewAudience = usePreviewAudience();

  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [estimatedRecipients, setEstimatedRecipients] = useState<number>(0);
  const [formData, setFormData] = useState<CampaignForm>({
    title: "",
    body: "",
    category: "general",
    priority: "default",
    dataPayload: {},
    audienceType: "all",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [alertState, setAlertState] = useState<{
    open: boolean;
    variant: "success" | "danger";
    title: string;
    description?: string;
  }>({open: false, variant: "success", title: ""});

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 200) {
      newErrors.title = "Title must be 200 characters or less";
    }

    if (!formData.body.trim()) {
      newErrors.body = "Body is required";
    } else if (formData.body.length > 1000) {
      newErrors.body = "Body must be 1000 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.audienceType === "individual" && !formData.targetUserId?.trim()) {
      newErrors.targetUserId = "User ID is required for individual targeting";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = async () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;

    if (currentStep === 2) {
      // Preview audience before moving to step 3
      try {
        const result = await previewAudience.mutateAsync({
          audienceType: formData.audienceType,
          audienceFilters: formData.audienceFilters,
        });
        setEstimatedRecipients(result.data?.estimatedRecipients ?? 0);
      } catch {
        setAlertState({open: true, variant: "danger", title: "Error", description: "Failed to preview audience"});
        return;
      }
    }

    setCurrentStep(prev => Math.min(3, prev + 1) as WizardStep);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1) as WizardStep);
  };

  const handleSubmit = async () => {
    try {
      const payload: CreateCampaignDto = {
        title: formData.title,
        body: formData.body,
        category: formData.category,
        priority: formData.priority,
        imageUrl: formData.imageUrl,
        actionUrl: formData.actionUrl,
        dataPayload: formData.dataPayload,
        audienceType: formData.audienceType,
        audienceFilters: formData.audienceFilters,
        targetUserId: formData.targetUserId,
      };

      const result = await createCampaign.mutateAsync(payload);
      setAlertState({
        open: true,
        variant: "success",
        title: "Campaign created",
        description: "Campaign created successfully!",
      });

      // Navigate to campaign detail
      if (result.data?.id) {
        navigate(PATHS.PUSH_CAMPAIGN_DETAIL(result.data.id));
      } else {
        navigate(PATHS.PUSH_CAMPAIGNS);
      }
    } catch {
      setAlertState({open: true, variant: "danger", title: "Error", description: "Failed to create campaign"});
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <BaseButton
          variant="ghost"
          size="sm"
          onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
          leftIcon={<ArrowLeft size={18} />}
        >
          Back
        </BaseButton>
        <div>
          <h1
            className="text-2xl font-bold"
            style={{color: "var(--color-text)"}}
          >
            Create Campaign
          </h1>
          <p
            className="text-sm mt-1"
            style={{color: "var(--color-text-secondary)"}}
          >
            Step {currentStep} of 3
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex gap-2">
        {[1, 2, 3].map(step => (
          <div
            key={step}
            className="flex-1 h-2 rounded-full transition-colors"
            style={{
              backgroundColor: step <= currentStep ? "var(--color-primary)" : "var(--color-surface-2)",
            }}
          />
        ))}
      </div>

      {/* Step Content */}
      <BaseCard padding="md">
        {currentStep === 1 && (
          <Step1Content
            formData={formData}
            onChange={data => setFormData(prev => ({...prev, ...data}))}
            errors={errors}
          />
        )}
        {currentStep === 2 && (
          <Step2Audience
            formData={formData}
            onChange={data => setFormData(prev => ({...prev, ...data}))}
            errors={errors}
          />
        )}
        {currentStep === 3 && (
          <Step3Review
            formData={formData}
            estimatedRecipients={estimatedRecipients}
          />
        )}

        {/* Navigation Buttons */}
        <div
          className="flex gap-3 justify-between pt-6 mt-6 border-t"
          style={{borderColor: "var(--color-surface-2)"}}
        >
          <div>
            {currentStep > 1 && (
              <BaseButton
                variant="ghost"
                onClick={handlePrevStep}
                leftIcon={<ArrowLeft size={18} />}
              >
                Previous
              </BaseButton>
            )}
          </div>

          <div className="flex gap-3">
            <BaseButton
              variant="ghost"
              onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
            >
              Cancel
            </BaseButton>

            {currentStep < 3 ? (
              <BaseButton
                variant="primary"
                onClick={handleNextStep}
                rightIcon={<ArrowRight size={18} />}
                disabled={previewAudience.isPending}
              >
                {currentStep === 2 && previewAudience.isPending ? "Loading..." : "Next"}
              </BaseButton>
            ) : (
              <BaseButton
                variant="primary"
                onClick={handleSubmit}
                disabled={createCampaign.isPending}
                leftIcon={<Send size={18} />}
              >
                {createCampaign.isPending ? "Creating..." : "Create Campaign"}
              </BaseButton>
            )}
          </div>
        </div>
      </BaseCard>

      <BaseAlert
        open={alertState.open}
        variant={alertState.variant}
        title={alertState.title}
        description={alertState.description}
        onClose={() => setAlertState(prev => ({...prev, open: false}))}
      />
    </div>
  );
};
